import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';

const TrustIndicators = () => {
  const { t } = useTranslation();

  const certifications = [
    { id: 'gdpr',     nameKey: 'landing.trust.gdprTitle', descKey: 'landing.trust.gdprDesc', icon: 'Shield' },
    { id: 'iso27001', nameKey: 'landing.trust.isoTitle',  descKey: 'landing.trust.isoDesc',  icon: 'Award' },
    { id: 'eu',       nameKey: 'landing.trust.euTitle',   descKey: 'landing.trust.euDesc',   icon: 'Server' },
    { id: 'soc2',     nameKey: 'landing.trust.soc2Title', descKey: 'landing.trust.soc2Desc', icon: 'CheckCircle' },
  ];

  const complianceFeatures = [
    { titleKey: 'landing.trust.reg1Title', descKey: 'landing.trust.reg1Desc', icon: 'FileText' },
    { titleKey: 'landing.trust.reg2Title', descKey: 'landing.trust.reg2Desc', icon: 'BarChart' },
    { titleKey: 'landing.trust.reg3Title', descKey: 'landing.trust.reg3Desc', icon: 'Lock' },
    { titleKey: 'landing.trust.reg4Title', descKey: 'landing.trust.reg4Desc', icon: 'Search' },
  ];

  const securityMeasures = [
    'landing.trust.measure1',
    'landing.trust.measure2',
    'landing.trust.measure3',
    'landing.trust.measure4',
    'landing.trust.measure5',
    'landing.trust.measure6',
  ];

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16">
          <div className="inline-flex items-center space-x-2 bg-success/10 text-success px-4 py-2 rounded-full text-sm font-medium">
            <Icon name="Shield" size={16} />
            <span>{t('landing.trust.badge')}</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
            {t('landing.trust.sectionTitle')}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {t('landing.trust.sectionSubtitle')}
          </p>
        </div>

        {/* Certifications Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {certifications.map((cert) => (
            <div
              key={cert.id}
              className="bg-white p-6 rounded-xl border border-border hover:shadow-lg transition-all duration-300 text-center"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name={cert.icon} size={24} className="text-primary" />
              </div>
              <h3 className="font-semibold text-foreground mb-2">{t(cert.nameKey)}</h3>
              <p className="text-sm text-muted-foreground">{t(cert.descKey)}</p>
            </div>
          ))}
        </div>

        {/* Compliance Features */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-foreground">
              {t('landing.trust.regulatoryHeader')}
            </h3>
            <div className="space-y-4">
              {complianceFeatures.map((feature, index) => (
                <div key={index} className="flex space-x-4 p-4 bg-white rounded-lg shadow-soft">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon name={feature.icon} size={18} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">{t(feature.titleKey)}</h4>
                    <p className="text-sm text-muted-foreground">{t(feature.descKey)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-foreground">
              {t('landing.trust.measuresHeader')}
            </h3>
            <div className="bg-white rounded-xl p-6 shadow-soft">
              <div className="space-y-3">
                {securityMeasures.map((measureKey, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Icon name="Check" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">{t(measureKey)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="bg-white rounded-2xl p-8 shadow-soft">
          <div className="text-center space-y-6">
            <h3 className="text-xl font-bold text-foreground">
              {t('landing.trust.recognizedHeader')}
            </h3>

            <div className="flex flex-wrap justify-center items-center gap-8">
              <div className="flex items-center space-x-2 px-4 py-2 bg-muted/50 rounded-lg">
                <Icon name="Award" size={16} className="text-primary" />
                <span className="text-sm font-medium text-foreground">{t('landing.trust.isoCertified')}</span>
              </div>
              <div className="flex items-center space-x-2 px-4 py-2 bg-muted/50 rounded-lg">
                <Icon name="Shield" size={16} className="text-success" />
                <span className="text-sm font-medium text-foreground">{t('landing.trust.gdprCompliant')}</span>
              </div>
              <div className="flex items-center space-x-2 px-4 py-2 bg-muted/50 rounded-lg">
                <Icon name="CheckCircle" size={16} className="text-blue-600" />
                <span className="text-sm font-medium text-foreground">{t('landing.trust.soc2Type2')}</span>
              </div>
              <div className="flex items-center space-x-2 px-4 py-2 bg-muted/50 rounded-lg">
                <Icon name="Globe" size={16} className="text-purple-600" />
                <span className="text-sm font-medium text-foreground">{t('landing.trust.euHosted')}</span>
              </div>
            </div>

            <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
              {t('landing.trust.recognizedFooter')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustIndicators;
